import React, { useState, useEffect } from 'react';
import { fetchAllUserTrades, getDayEndReport, sendDayReportToTelegram } from '../services/apiService';
import { netRsOf } from '../utils/tradeCosts';
import '../styles/AdminTradesDashboard.css';

const todayIST = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
};

const fmt = (v, decimals = 2) =>
  v != null ? Number(v).toFixed(decimals) : '—';

const AdminTradesDashboard = ({ currentUser, onClose }) => {
  const [trades, setTrades]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [selectedDate, setSelectedDate] = useState(todayIST());
  const [reportText, setReportText]   = useState('');
  const [sending, setSending]         = useState(false);
  const [sendResult, setSendResult]   = useState('');
  const [loadingReport, setLoadingReport] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => {
    loadTrades();
    // Auto-refresh every 30 s so new VIEWER trades appear without a manual click.
    const interval = setInterval(loadTrades, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrades = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllUserTrades(currentUser.username);
      setTrades(data);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load trades.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewReport = async () => {
    setLoadingReport(true);
    setSendResult('');
    try {
      const data = await getDayEndReport(currentUser.username, selectedDate);
      setReportText(data.report || '');
    } catch (e) {
      setReportText('Error loading report: ' + (e?.response?.data?.error || e.message));
    } finally {
      setLoadingReport(false);
    }
  };

  const handleSendToTelegram = async () => {
    setSending(true);
    setSendResult('');
    try {
      await sendDayReportToTelegram(currentUser.username, selectedDate);
      setSendResult('ok');
    } catch (e) {
      setSendResult('err');
    } finally {
      setSending(false);
    }
  };

  // Filter displayed trades by selectedDate (client-side for the table view): a trade belongs to
  // the day it was entered and to the day it closed, so a positional trade closed today shows today.
  // Older rows carry a zone suffix ("…+05:30[Asia/Kolkata]") that Date can't parse; their
  // stored date prefix is already IST.
  const istDate = ts => {
    if (!ts) return null;
    const d = new Date(String(ts).replace(/\[.*\]$/, ''));
    return !isNaN(d.getTime()) ? d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) : String(ts).slice(0, 10);
  };
  const filteredTrades = trades.filter(t =>
    !selectedDate || istDate(t.timestamp) === selectedDate || istDate(t.exitTime) === selectedDate
  );
  // Net ₹ after charges, closed trades only. Each user is a separate book (SYSTEM = the engine),
  // so the day's total is per user. A positional trade counts on the day it closed, not the
  // day it was entered — the same day the EOD report books it.
  const closedNet = t => (t.outcome && t.outcome !== 'OPEN' ? netRsOf(t) : null);
  const netByUser = {};
  filteredTrades.forEach(t => {
    const n = closedNet(t);
    if (n == null || (t.exitTime && istDate(t.exitTime) !== selectedDate)) return;
    netByUser[t.username || '—'] = (netByUser[t.username || '—'] ?? 0) + n;
  });
  const rupees = n => (
    <span style={{ color: n >= 0 ? '#86efac' : '#fca5a5' }}>
      {n >= 0 ? '+' : '−'}₹{Math.abs(Math.round(n)).toLocaleString('en-IN')}
    </span>
  );

  const fmtIstTime = (ts, withDate) => new Date(String(ts).replace(/\[.*\]$/, '')).toLocaleString('en-IN', {
    ...(withDate ? { day: '2-digit', month: 'short' } : {}),
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  });

  const statusClass = (outcome) => {
    if (outcome === 'WIN')  return 'atd-status atd-status-win';
    if (outcome === 'LOSS') return 'atd-status atd-status-loss';
    return 'atd-status atd-status-open';
  };

  const dirClass = (dir) => {
    if (!dir) return '';
    return dir.toLowerCase() === 'call' ? 'atd-dir atd-dir-call' : 'atd-dir atd-dir-put';
  };

  const pnlDisplay = (t) => {
    if (t.outcome === 'OPEN') return <span style={{ color: '#fdba74' }}>open</span>;
    if (t.pnlPts == null) return '—';
    const val = Number(t.pnlPts);
    return (
      <span style={{ color: val >= 0 ? '#86efac' : '#fca5a5' }}>
        {val >= 0 ? '+' : ''}{val.toFixed(1)}
      </span>
    );
  };

  return (
    <div className="atd-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="atd-modal">
        <div className="atd-header">
          <span>Admin — All Trades Dashboard</span>
          <button className="atd-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Toolbar ─────────────────────────────────────────────────────── */}
        <div className="atd-toolbar">
          <label>Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
          />
          <button className="atd-btn" onClick={loadTrades} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && <div className="atd-error">{error}</div>}

        <div className="atd-info">
          Showing {filteredTrades.length} trade{filteredTrades.length !== 1 ? 's' : ''}
          {selectedDate ? ` for ${selectedDate}` : ''} (of {trades.length} total)
          {Object.keys(netByUser).length > 0 && (
            <span style={{ marginLeft: 12 }}>
              · Net P&amp;L{' '}
              {Object.entries(netByUser).sort(([a], [b]) => a.localeCompare(b)).map(([u, n], i) => (
                <span key={u}>{i > 0 && ', '}<span style={{ color: '#a5b4fc' }}>{u}</span> {rupees(n)}</span>
              ))}
            </span>
          )}
          {lastRefreshed && (
            <span style={{ marginLeft: 12, color: '#64748b', fontSize: 11 }}>
              · refreshed {lastRefreshed.toLocaleTimeString('en-IN', {
                  hour: '2-digit', minute: '2-digit', second: '2-digit',
                  hour12: true, timeZone: 'Asia/Kolkata',
                })} IST · auto-refreshes every 30s
            </span>
          )}
        </div>

        {/* ── Trades table ─────────────────────────────────────────────────── */}
        <div className="atd-table-wrap">
          <table className="atd-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Symbol</th>
                <th>Dir</th>
                <th>Verdict</th>
                <th>Strike</th>
                <th>Entry ₹</th>
                <th>Exit ₹</th>
                <th>P&amp;L</th>
                <th title="After brokerage, STT, exchange, SEBI, stamp and GST">Net ₹</th>
                <th>Status</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.map((t, i) => (
                <tr key={t.id || i}>
                  <td style={{ fontWeight: 600, color: '#a5b4fc' }}>{t.username || '—'}</td>
                  <td>{t.symbol || '—'}</td>
                  <td>
                    {t.dir
                      ? <span className={dirClass(t.dir)}>{t.dir.toUpperCase()}</span>
                      : '—'}
                  </td>
                  <td style={{ fontSize: '11px', color: '#94a3b8' }}>{t.finalVerdict || t.verdict || '—'}</td>
                  <td>{t.entryStrike != null ? t.entryStrike : '—'}</td>
                  <td>{t.entryLTP != null ? fmt(t.entryLTP) : '—'}</td>
                  <td>{t.exitLTP  != null ? fmt(t.exitLTP)  : '—'}</td>
                  <td>{pnlDisplay(t)}</td>
                  <td>{closedNet(t) != null ? rupees(closedNet(t)) : '—'}</td>
                  <td>
                    <span className={statusClass(t.outcome)}>
                      {t.outcome || 'OPEN'}
                    </span>
                  </td>
                  <td style={{ fontSize: '11px', color: '#64748b' }}>
                    {t.timestamp
                      ? fmtIstTime(t.timestamp, istDate(t.timestamp) !== selectedDate)
                      : '—'}
                    {t.exitTime && istDate(t.exitTime) !== istDate(t.timestamp)
                      && <> → {fmtIstTime(t.exitTime, istDate(t.exitTime) !== selectedDate)}</>}
                  </td>
                </tr>
              ))}
              {filteredTrades.length === 0 && !loading && (
                <tr>
                  <td colSpan={11} className="atd-empty">
                    No trades found{selectedDate ? ` for ${selectedDate}` : ''}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Day report section ───────────────────────────────────────────── */}
        <div className="atd-report-section">
          <div className="atd-report-actions">
            <button
              className="atd-btn atd-btn-secondary"
              onClick={handlePreviewReport}
              disabled={loadingReport}
            >
              {loadingReport ? 'Loading…' : 'Preview Day Report'}
            </button>
            <button
              className="atd-btn atd-btn-telegram"
              onClick={handleSendToTelegram}
              disabled={sending}
            >
              {sending ? 'Sending…' : 'Send to Telegram'}
            </button>
            {sendResult === 'ok' && (
              <span className="atd-send-result ok">Sent successfully</span>
            )}
            {sendResult === 'err' && (
              <span className="atd-send-result err">Send failed</span>
            )}
          </div>
          {reportText && (
            <pre className="atd-report-text">{reportText}</pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTradesDashboard;
